import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle2, XCircle, MailCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level guard to survive React StrictMode double-invocation.
const _consumed = new Map();

/** Lands on /verify-email?token=... → calls backend, shows result. */
export const VerifyEmailPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [status, setStatus] = useState('processing');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMsg('Missing verification token');
      return;
    }
    let promise = _consumed.get(token);
    if (!promise) {
      promise = axios.post(`${API}/auth/verify-email`, { token })
        .then((r) => ({ status: 'success', msg: r.data?.message || 'Email verified.' }))
        .catch((e) => ({
          status: 'error',
          msg: e.response?.data?.detail || 'Verification failed',
        }));
      _consumed.set(token, promise);
    }
    promise.then((r) => {
      setStatus(r.status);
      setMsg(r.msg);
    });
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-4" data-testid="verify-email-page">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center">
          {status === 'processing' && (
            <>
              <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-zinc-900">Verifying your email…</h2>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-zinc-900">Email verified</h2>
              <p className="text-sm text-zinc-500 mt-2 mb-6">{msg}</p>
              <Button onClick={() => navigate('/login')} data-testid="verify-email-go-to-login">
                Continue to sign in
              </Button>
            </>
          )}
          {status === 'error' && (
            <>
              <XCircle className="w-10 h-10 text-red-600 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-zinc-900">Couldn't verify</h2>
              <p className="text-sm text-zinc-500 mt-2 mb-6">{msg}</p>
              <Button onClick={() => navigate('/verify-email-pending')}>
                Resend verification email
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/** Shown right after signup or when the user lands on a protected page while
 * still unverified. Lets them resend the link. */
export const VerifyEmailPendingPage = () => {
  const [resending, setResending] = useState(false);
  const [resentAt, setResentAt] = useState(null);
  const [email, setEmail] = useState('');

  // Try to grab the user's email from /auth/me if logged in.
  useEffect(() => {
    const tok = localStorage.getItem('token');
    if (!tok) return;
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${tok}` } })
      .then((r) => setEmail(r.data?.email || ''))
      .catch(() => {});
  }, []);

  const resend = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      setResentAt(new Date());
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-4" data-testid="verify-email-pending-page">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center">
          <div className="inline-flex w-12 h-12 rounded-xl bg-indigo-100 text-indigo-600 items-center justify-center mb-4">
            <MailCheck className="w-6 h-6" />
          </div>
          <h2 className="text-xl font-semibold text-zinc-900 mb-2">Check your inbox</h2>
          <p className="text-sm text-zinc-500 mb-6">
            We sent a verification link to <strong>{email || 'your email'}</strong>.
            Click it to activate your account. The link expires in 24 hours.
          </p>
          <form onSubmit={resend} className="space-y-3">
            {!email && (
              <input
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-zinc-200 rounded-md text-sm"
                required
                data-testid="verify-email-resend-input"
              />
            )}
            <Button
              type="submit"
              variant="outline"
              className="w-full"
              disabled={resending || !email}
              data-testid="verify-email-resend-btn"
            >
              {resending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Resend verification email
            </Button>
          </form>
          {resentAt && (
            <p className="text-xs text-emerald-600 mt-3">A new verification email is on its way.</p>
          )}
          <p className="text-xs text-zinc-400 mt-6">
            Already verified? <Link to="/login" className="text-indigo-600 hover:underline">Sign in</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmailPage;
